"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import type { CreateWaveDatesConfig } from "@/types/waves.types";
import type { ApiWaveType } from "@/generated/models/ApiWaveType";
import StartDates from "./StartDates";
import Decisions from "./Decisions";
import RollingEndDate from "./RollingEndDate";
import { adjustDatesAfterSubmissionChange, calculateEndDate, clampRollingEndDate, validateDateSequence } from "../services/waveDecisionService";
import { CREATE_WAVE_VALIDATION_ERROR } from "@/helpers/waves/create-wave.validation";
import { Time } from "@/helpers/time";
interface CreateWaveDatesRankProps {
  readonly waveType: ApiWaveType;
  readonly dates: CreateWaveDatesConfig;
  readonly errors: CREATE_WAVE_VALIDATION_ERROR[];
  readonly setDates: (dates: CreateWaveDatesConfig) => void;
}
export default function CreateWaveDatesRank(t0) {
  const $ = _c(49);
  const {
    waveType,
    dates,
    errors,
    setDates
  } = t0;
  const isRollingMode = dates.isRolling;
  let t1;
  if ($[0] !== dates.isRolling) {
    t1 = {
      start: true,
      decisions: false,
      rolling: dates.isRolling
    };
    $[0] = dates.isRolling;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [expandedSections, setExpandedSections] = useState(t1);
  const [hasAutoCollapsedStart, setHasAutoCollapsedStart] = useState(false);
  const rankFutureDateError = CREATE_WAVE_VALIDATION_ERROR.RANK_DECISION_TIME_MUST_BE_IN_FUTURE;
  let t2;
  if ($[2] !== errors) {
    t2 = errors.includes(rankFutureDateError);
    $[2] = errors;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const hasRankFutureDateError = t2;
  let t3;
  if ($[4] !== hasRankFutureDateError) {
    t3 = hasRankFutureDateError ? Time.currentMillis() : null;
    $[4] = hasRankFutureDateError;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const now = t3;
  const isFirstDecisionTimeInPast = now !== null && dates.firstDecisionTime <= now;
  let t4;
  if ($[6] !== dates.endDate || $[7] !== dates.isRolling || $[8] !== now) {
    t4 = now !== null && dates.isRolling && (dates.endDate === null || !Number.isFinite(dates.endDate) || dates.endDate <= now);
    $[6] = dates.endDate;
    $[7] = dates.isRolling;
    $[8] = now;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const isRollingEndDateInPast = t4;
  let t5;
  if ($[10] !== dates.isRolling || $[11] !== errors || $[12] !== hasRankFutureDateError || $[13] !== isFirstDecisionTimeInPast) {
    let t6;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = error => error !== rankFutureDateError;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    const errorsWithoutRankFutureDate = errors.filter(t6);
    t5 = hasRankFutureDateError && (!dates.isRolling || isFirstDecisionTimeInPast) ? [...errorsWithoutRankFutureDate, rankFutureDateError] : errorsWithoutRankFutureDate;
    $[10] = dates.isRolling;
    $[11] = errors;
    $[12] = hasRankFutureDateError;
    $[13] = isFirstDecisionTimeInPast;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const decisionErrors = t5;
  let t6;
  if ($[16] !== hasRankFutureDateError || $[17] !== isRollingEndDateInPast) {
    t6 = hasRankFutureDateError && isRollingEndDateInPast ? [rankFutureDateError] : [];
    $[16] = hasRankFutureDateError;
    $[17] = isRollingEndDateInPast;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const rollingEndDateErrors = t6;
  let t7;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = sectionName => {
      setExpandedSections(prev => ({
        ...prev,
        [sectionName]: !prev[sectionName]
      }));
    };
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const toggleSection = t7;
  let t8;
  if ($[20] !== dates || $[21] !== setDates) {
    t8 = nextDates => {
      let normalizedDates = nextDates;
      if (nextDates.submissionStartDate !== dates.submissionStartDate) {
        const normalizedTimeline = adjustDatesAfterSubmissionChange(dates, nextDates.submissionStartDate);
        normalizedDates = {
          ...nextDates,
          submissionStartDate: normalizedTimeline.submissionStartDate,
          votingStartDate: normalizedTimeline.votingStartDate,
          firstDecisionTime: normalizedTimeline.firstDecisionTime
        };
      }
      if (normalizedDates.isRolling) {
        normalizedDates = {
          ...normalizedDates,
          endDate: clampRollingEndDate(normalizedDates)
        };
      } else {
        normalizedDates = {
          ...normalizedDates,
          endDate: calculateEndDate(normalizedDates)
        };
      }
      const validationErrors = validateDateSequence(normalizedDates);
      if (validationErrors.length > 0) {
        console.warn("Date validation errors:", validationErrors);
      }
      setDates(normalizedDates);
    };
    $[20] = dates;
    $[21] = setDates;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const commitDates = t8;
  let t9;
  if ($[23] !== hasAutoCollapsedStart) {
    t9 = () => {
      if (!hasAutoCollapsedStart) {
        setExpandedSections(_temp);
        setHasAutoCollapsedStart(true);
      }
    };
    $[23] = hasAutoCollapsedStart;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const handleDecisionsInteraction = t9;
  let t10;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setExpandedSections(_temp2);
    };
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const handleRollingEnabled = t10;
  let t11;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => toggleSection("start");
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== commitDates || $[28] !== dates || $[29] !== expandedSections.start || $[30] !== waveType) {
    t12 = <StartDates waveType={waveType} dates={dates} setDates={commitDates} isExpanded={expandedSections.start} setIsExpanded={t11} />;
    $[27] = commitDates;
    $[28] = dates;
    $[29] = expandedSections.start;
    $[30] = waveType;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => toggleSection("decisions");
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== commitDates || $[34] !== dates || $[35] !== decisionErrors || $[36] !== expandedSections.decisions || $[37] !== handleDecisionsInteraction) {
    t14 = <Decisions dates={dates} errors={decisionErrors} setDates={commitDates} onRollingEnabled={handleRollingEnabled} isExpanded={expandedSections.decisions} setIsExpanded={t13} onInteraction={handleDecisionsInteraction} />;
    $[33] = commitDates;
    $[34] = dates;
    $[35] = decisionErrors;
    $[36] = expandedSections.decisions;
    $[37] = handleDecisionsInteraction;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== commitDates || $[40] !== dates || $[41] !== expandedSections.rolling || $[42] !== isRollingMode || $[43] !== rollingEndDateErrors) {
    t15 = dates.subsequentDecisions.length > 0 && isRollingMode && <RollingEndDate dates={dates} errors={rollingEndDateErrors} setDates={commitDates} isExpanded={expandedSections.rolling} setIsExpanded={() => toggleSection("rolling")} />;
    $[39] = commitDates;
    $[40] = dates;
    $[41] = expandedSections.rolling;
    $[42] = isRollingMode;
    $[43] = rollingEndDateErrors;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== t12 || $[46] !== t14 || $[47] !== t15) {
    t16 = <div className="tw-space-y-4">{t12}{t14}{t15}</div>;
    $[45] = t12;
    $[46] = t14;
    $[47] = t15;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  return t16;
}
function _temp2(prev_1) {
  return prev_1.rolling ? prev_1 : {
    ...prev_1,
    rolling: true
  };
}
function _temp(prev_0) {
  return {
    ...prev_0,
    start: false
  };
}